import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { apiRequest } from '@/lib/queryClient';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

type VerificationStatus = 'loading' | 'success' | 'error';

interface VerificationResult {
  success?: boolean;
  message?: string;
  error?: string;
  tenant?: {
    id: number;
    name: string;
    domain: string | null;
  };
}

export default function VerifyTenant() {
  const [status, setStatus] = useState<VerificationStatus>('loading');
  const [result, setResult] = useState<VerificationResult | null>(null);
  const [, navigate] = useLocation();

  // Parse the token from the URL
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');

  useEffect(() => {
    const verifyTenant = async () => {
      if (!token) {
        setStatus('error');
        setResult({
          success: false,
          message: 'Verification token is missing',
          error: 'token_missing'
        });
        return;
      }

      try {
        const response = await apiRequest<Response>(`/api/tenants/verify?token=${token}`, {
          method: 'GET'
        });

        if (!response.ok) {
          const errorData = await response.json();
          setStatus('error');
          setResult({
            success: false,
            message: errorData.message || 'Verification failed',
            error: errorData.error || 'unknown_error'
          });
          return;
        }

        const data = await response.json();
        setStatus('success');
        setResult(data);
      } catch (error) {
        console.error('Error verifying tenant:', error);
        setStatus('error');
        setResult({
          success: false,
          message: 'An unexpected error occurred',
          error: 'network_error'
        });
      }
    };

    verifyTenant();
  }, [token]);

  const redirectToLogin = () => {
    navigate('/login-page');
  };

  return (
    <div className="h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Organization Verification</CardTitle>
          <CardDescription>
            Validating your organization account
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center p-6">
          {status === 'loading' && (
            <div className="text-center p-6">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
              <p className="text-gray-500">Processing your verification request...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center p-6">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Verification Successful!</h3>
              <p className="mb-4">{result?.message || 'Your organization has been verified successfully.'}</p>
              <p className="text-gray-600">
                {result?.tenant?.name ? `Organization: ${result.tenant.name}` : ''}
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center p-6">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Verification Failed</h3>
              <p className="mb-4">{result?.message || 'We could not verify your organization.'}</p>
              {result?.error === 'token_invalid' && (
                <p className="text-gray-600 text-sm">
                  The verification link may have expired or is invalid. Please contact your administrator.
                </p>
              )}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-center pb-6">
          <Button 
            onClick={redirectToLogin} 
            className="w-full max-w-xs"
          >
            {status === 'success' ? 'Go to Login' : 'Back to Login'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}